import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material";
import noop from "lodash/noop";
import type { FC } from "react";
import type { ProductResponse } from "../../admin/products/productsApi";
import { useProducts } from "../../admin/products/useProducts";
import type { Currency } from "../../data/BaseEntity";
import { Price } from "../../misc/price";
import CalculationProductName from "../calculationProduct/CalculationProductName";
import type {
  SavedCalculationPriceValue,
  SavedCalculationProduct,
  SavedCalculationProductOption,
} from "../calculationTypes";
import SurroundingDaysPrice from "../calculationVersionEdit/productsPriceSummary/SurroundingDaysPrice";
import ProductsTotalPrice from "./ProductsTotalPrice";

interface OptionRowProps {
  readonly option: SavedCalculationProductOption;
  readonly optionName: string | undefined;
  readonly currency: Currency;
  readonly exchangeRate: number;
  readonly hideProductPrices: boolean;
}

const OptionCells: FC<OptionRowProps> = ({ option, optionName, currency, exchangeRate, hideProductPrices }) => {
  const price = Price.fromNullable(option.price).multiply(exchangeRate).round();
  return (
    <>
      <TableCell>
        <Typography>{optionName ?? "nd."}</Typography>
      </TableCell>
      {!hideProductPrices && (
        <TableCell>
          <Typography>{price.isPresent() ? `${price.value} ${currency}` : "n.d"}</Typography>
        </TableCell>
      )}
      <TableCell>
        <Typography>{`${option.quantity} szt.`}</Typography>
      </TableCell>
    </>
  );
};

interface ProductRowProps {
  readonly hideProductPrices: boolean;
  readonly value: SavedCalculationProduct;
  readonly product: ProductResponse | undefined;
  readonly calculationCurrency: Currency;
  readonly calculationMargin: number;
  readonly participantsNumber: number;
}

const ProductRow: FC<ProductRowProps> = ({
  value,
  product,
  hideProductPrices,
  calculationCurrency,
  calculationMargin,
  participantsNumber,
}) => {
  const optionNames = new Map((product?.options ?? []).map((o) => [o.id, o.name]));
  const [firstOption, ...otherOptions] = value.options;
  const rowSpan = value.options.length > 0 ? value.options.length : 1;

  return (
    <>
      <TableRow>
        <TableCell rowSpan={rowSpan}>
          <CalculationProductName
            selectedProduct={value}
            productDetails={product}
            calculationCurrency={calculationCurrency}
            calculationMargin={calculationMargin}
            participantsNumber={participantsNumber}
            onReplace={noop}
            readonly
          >
            <Typography>{product?.name}</Typography>
          </CalculationProductName>
        </TableCell>
        <TableCell rowSpan={rowSpan}>
          <Typography>{value.date}</Typography>
        </TableCell>
        {firstOption ? (
          <OptionCells
            option={firstOption}
            optionName={optionNames.get(firstOption.id)}
            currency={calculationCurrency}
            exchangeRate={1}
            hideProductPrices={hideProductPrices}
          />
        ) : (
          <>
            <TableCell />
            {!hideProductPrices && <TableCell />}
            <TableCell />
          </>
        )}
        {!hideProductPrices && (
          <TableCell rowSpan={rowSpan} align="right">
            <Typography data-testid="product-price">
              {Price.fromNullable(value.price).toDisplayValue(calculationCurrency)}
            </Typography>
          </TableCell>
        )}
      </TableRow>
      {otherOptions.map((option, idx) => (
        <TableRow key={`${option.id}:${idx}`}>
          <OptionCells
            option={option}
            optionName={optionNames.get(option.id)}
            currency={calculationCurrency}
            exchangeRate={1}
            hideProductPrices={hideProductPrices}
          />
        </TableRow>
      ))}
    </>
  );
};

interface Props {
  readonly hideProductPrices: boolean;
  readonly selectedProducts: SavedCalculationProduct[];
  readonly currency: Currency;
  readonly margin: number;
  readonly price: SavedCalculationPriceValue;
  readonly participantsNumber: number;
}

const ReadonlyCalculationProducts: FC<Props> = ({
  hideProductPrices,
  selectedProducts,
  currency,
  margin,
  price,
  participantsNumber,
}) => {
  const { products } = useProducts();
  return (
    <Table data-testid="calculation-products">
      <TableHead>
        <TableRow>
          <TableCell>Produkt</TableCell>
          <TableCell>Data</TableCell>
          <TableCell>Nazwa opcji</TableCell>
          {!hideProductPrices && <TableCell>Cena jednostkowa</TableCell>}
          <TableCell>Ilość</TableCell>
          {!hideProductPrices && <TableCell align="right">Cena</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {selectedProducts.map((p) => (
          <ProductRow
            key={`${p.id}#${p.date}`}
            value={p}
            product={products.find((pr) => pr.id === p.id)}
            hideProductPrices={hideProductPrices}
            calculationCurrency={currency}
            calculationMargin={margin}
            participantsNumber={participantsNumber}
          />
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell colSpan={2} />
          <TableCell colSpan={hideProductPrices ? 2 : 4}>
            <SurroundingDaysPrice
              selectedProducts={selectedProducts}
              availableProducts={products}
              currency={currency}
              margin={margin}
              numberOfPeople={participantsNumber}
            >
              <ProductsTotalPrice price={price} currency={currency} numberOfPeople={participantsNumber} />
            </SurroundingDaysPrice>
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
};

export default ReadonlyCalculationProducts;
